import React from 'react';
import { Box, Typography } from '@mui/material';
import { getQuestionStateClass } from './helpers';
import { ChecklistMarker } from './ChecklistMarker';

/**
 * How a row is announced: the question it opens, and — once a quiz has been
 * taken or marked — how that question stands.
 * @param {Number} number The question's position in the quiz, starting at 1
 * @param {Object} result One entry from gradeQuiz, or undefined in the editor
 * @param {Boolean} isGraded Whether the quiz has been submitted
 * @returns {String} The row's label
 */
const getRowLabel = (number, result, isGraded) => {
  if (!result) return `Go to question ${number}`;

  if (isGraded) return `Go to question ${number}, ${result.isCorrect ? 'correct' : 'incorrect'}`;
  return `Go to question ${number}, ${result.isAnswered ? 'answered' : 'not answered'}`;
};

/**
 * One question in a list of them: how it stands, its number, and its text cut to
 * a single line. The sheet a phone opens from the bar and the results page's own
 * list are both made of these, so a question reads the same wherever it is
 * listed and picking one always means the same thing.
 *
 * Props:
 *   id       (string)   - The row's element ID.
 *   number   (number)   - The question's position in the quiz, starting at 1.
 *   text     (string)   - The question itself.
 *   result   (object)   - One entry from gradeQuiz. Left out by the editor,
 *                         which has neither answers nor marks.
 *   isGraded (bool)     - Whether the quiz has been submitted.
 *   isActive (bool)     - Whether this is the question on screen.
 *   onSelect (function) - Open this question.
 */
export const QuestionListRow = ({ id, number, text, result, isGraded, isActive, onSelect }) => (
  <Box
    className={`mss__questionListRow ${result ? getQuestionStateClass(result, isGraded) : ''} ${
      isActive ? 'active' : ''
    }`}
    id={id}
    role="button"
    tabIndex={0}
    aria-current={isActive ? 'true' : undefined}
    /* The marker is decorative, so the state it conveys goes here. */
    aria-label={getRowLabel(number, result, isGraded)}
    onClick={onSelect}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect();
      }
    }}
  >
    {/* The editor marks nothing: a question there is neither right nor answered. */}
    {result && (
      <ChecklistMarker
        isGraded={isGraded}
        isCorrect={result.isCorrect}
        isAnswered={result.isAnswered}
      />
    )}
    <Typography className="rowNumber">{number}.</Typography>
    {/* Truncated to one line; a list of questions is not a second copy of the quiz. */}
    <Typography className="rowText">{text}</Typography>
  </Box>
);
